import { apiClient as api } from './apiClient';

/**
 * Seguimiento de documentos en firma digital (FDoc + Signaturit).
 *
 * Espejo EXACTO de `dbo.VW_FDOC_FIRMA_ESTADO`. La vista es la frontera anti-PII del módulo: los
 * eventos de Signaturit llevan nombre y correo de los firmantes y el panel no los necesita para
 * pintar un estado, así que el backend tiene SELECT solo sobre la vista.
 *
 * Ampliación 2026-09-10: la vista ya trae tipo de documento, responsable/solicitante y nº de
 * firmantes/firmados — son datos del REGISTRO y del EMPLEADO que lo generó, no de los clientes que
 * firman. Nombre y correo de los FIRMANTES siguen sin estar aquí: viven en
 * `VW_FDOC_FIRMA_CRONOLOGIA` (ver `getCronologia`), que solo puede llamar el perfil de Sistemas.
 */
export interface FdocFirmaEstado {
  /** Id del registro del documento en GC_FDOC_REGISTRO. Único identificador que llega SIEMPRE,
   * lo vea quien lo vea — úsalo como `rowKey` de la tabla, no `idSignaturit` (ver más abajo). */
  idRegistro: number;
  /**
   * Id del sobre en Signaturit. Detalle técnico: el backend solo lo manda al perfil de Sistemas
   * (`SeguimientoFdocVisibilidad.PuedeVerDetalleTecnico` en GCvscBE) — es la llave del sobre en la
   * consola de Signaturit, donde SÍ están nombre y correo de los firmantes. Para el resto de
   * perfiles llega `null`/ausente; el front lo usa como señal para no pintar esa columna, en vez
   * de duplicar la lista de roles en el cliente.
   */
  idSignaturit?: string | null;
  /** 'sandbox' | 'produccion'. Mismo gate que `idSignaturit`. */
  entorno?: string | null;
  /** 'sequential' | 'parallel', si se registró. Mismo gate que `idSignaturit`. */
  modoFirma?: string | null;
  /** Fecha de creación de la solicitud, en UTC y sin sufijo Z (ver `fechaUtc` más abajo). */
  fechaEnvio: string;
  /** Tipo de documento del registro (p. ej. «Solicitud de financiación»). */
  tipoDocumento?: string | null;
  /** Solo en presupuestos: código del servicio (PVA, PTF, SAJAF, PVP, PVE). Null en el resto. */
  codigoServicioPresupuesto?: string | null;
  /** Solo en presupuestos: epígrafe del servicio («Transmisión asistida»). */
  servicioPresupuesto?: string | null;
  /** Nombre del empleado que generó el documento. Dato del empleado, no del cliente firmante. */
  solicitanteNombre?: string | null;
  /** Object id de Entra del solicitante. Clave estable para filtrar (el nombre puede repetirse). */
  solicitanteOid?: string | null;
  /** CCL de la operación asociada, si la tiene. */
  cclOperacion?: string | null;
  /** pendiente · enviado · entregado · abierto · firmado · completado, o el tipo de incidencia. */
  estado: string;
  /** Punto del recorrido alcanzado por el evento más avanzado. Interno; NO es el avance a pintar. */
  avance: number;
  /** Nº de destinatarios detectados. 0 mientras no llegue ningún evento (no confundir con "sin firmantes"). */
  numFirmantes: number;
  /** Nº de destinatarios que ya han firmado. */
  numFirmados: number;
  /** Desenlace malo: rechazado, caducado, cancelado o correo rebotado. */
  incidencia: boolean;
  fechaUltimoEvento?: string | null;
  numEventos: number;
}

/** Filtros que la vista puede responder HOY. Ni uno más: un filtro que no se aplica engaña. */
export interface SeguimientoFdocFiltro {
  estado?: string;
  incidencia?: boolean;
  entorno?: string;
  /** ISO 8601, inclusive. */
  desde?: string;
  /** ISO 8601, inclusive. */
  hasta?: string;
  /** Solo busca por id de registro o id de Signaturit — la vista no expone más texto. */
  texto?: string;
  /** Tipo de documento exacto (DS_TIPO_DOCUMENTO). */
  tipoDocumento?: string;
  /** Oid del responsable/solicitante (ID_SOLICITANTE_OID). Filtro por oid, no por nombre. */
  solicitanteOid?: string;
  limite?: number;
}

/**
 * Un hito de la cronología: un evento de un destinatario concreto. Espejo de
 * `VW_FDOC_FIRMA_CRONOLOGIA`, que SÍ expone PII (nombre y correo del firmante). Solo llega al front
 * si `getCronologia` responde 200 — el backend deniega con 403 a cualquiera que no sea Sistemas.
 */
export interface FdocFirmaCronologiaEvento {
  idRegistro: number;
  idSignaturit: string;
  /** Orden de firma del destinatario dentro del sobre (firma secuencial). Nulo en sobres anteriores
   * a la tabla de destinatarios: esos van al final, ordenados por fecha del primer evento. */
  orden?: number | null;
  /** Id del documento dentro del sobre: identifica al DESTINATARIO (una fila por persona). */
  idDocumento?: string | null;
  /** True si el destinatario es un firmante de la casa (Farmaconsulting), no un cliente. */
  esFct: boolean;
  /** Nombre del destinatario/firmante. PII. */
  destinatario?: string | null;
  /** Correo del destinatario/firmante. PII. */
  email?: string | null;
  /** Tipo de evento tal como lo manda Signaturit. Nulo si el destinatario aún no ha recibido nada
   * (en espera, en firma secuencial). */
  tipo?: string | null;
  /** Nula si el destinatario aún está en espera. */
  fechaEvento?: string | null;
  fechaRecepcion?: string | null;
  tipoDocumento?: string | null;
  solicitanteNombre?: string | null;
  /** Punto del recorrido: 1 enviado · 2 entregado · 3 abierto · 4 firmado · 5 completado; 0 = otro. */
  ordenHito: number;
  /** True si el evento es un desenlace malo: un FINAL, no un hito del recorrido. */
  incidencia: boolean;
}

/**
 * Estados del recorrido, en orden de avance. Se usan para el desplegable y para el color del tag.
 * Los estados de incidencia NO están aquí: los nombra Signaturit y el catálogo puede crecer, así
 * que se tratan por el flag `incidencia`, no por una lista cerrada que quedaría desfasada.
 */
export const ESTADOS_RECORRIDO = [
  'pendiente',
  'enviado',
  'entregado',
  'abierto',
  'firmado',
  'completado',
] as const;

/** Etiquetas legibles de los estados de incidencia conocidos. Si llega uno nuevo, se muestra crudo. */
export const ETIQUETAS_INCIDENCIA: Record<string, string> = {
  document_declined: 'Rechazado',
  document_expired: 'Caducado',
  document_canceled: 'Cancelado',
  email_bounced: 'Correo rebotado',
};

const buildQuery = (filtro: SeguimientoFdocFiltro): string => {
  const parts = Object.entries(filtro)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join('&')}` : '';
};

/** Documentos en firma con su estado actual, más recientes primero. */
export const getFirmas = async (filtro: SeguimientoFdocFiltro = {}): Promise<FdocFirmaEstado[]> => {
  const { data } = await api.get<FdocFirmaEstado[]>(`/api/seguimiento-fdoc/firmas${buildQuery(filtro)}`);
  return data;
};

/**
 * Cronología completa de firma de un documento. Solo responde 200 al perfil de Sistemas; el resto
 * recibe 403 (el backend lo deniega por rol, no por convención del front — ver
 * `SeguimientoFdocVisibilidad.PuedeVerCronologia` en GCvscBE). El llamante debe tratar el 403 como
 * un estado legítimo ("no tienes acceso"), no como un fallo de red.
 */
export const getCronologia = async (idRegistro: number): Promise<FdocFirmaCronologiaEvento[]> => {
  const { data } = await api.get<FdocFirmaCronologiaEvento[]>(
    `/api/seguimiento-fdoc/firmas/${idRegistro}/cronologia`,
  );
  return data;
};
